import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  endpoints,
  type PlanSummary,
  type CreatePlanRequest,
} from "@/lib/api";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/Table";

// 投放工作台 (Campaign Planning Workbench) — v2.12.0
// One plan = one agency-list evaluation round, single-platform. Each plan
// imports N candidate channels, the operator filters down to ~10, exports
// to Claude Chat for final pick, then promotes the selected set into a
// new campaign (with N campaign_channels under it).
//
// This list page mirrors Campaigns.tsx layout: status filter + page +
// table; row click → detail; "+ 新建" opens a modal.

const STATUSES = [
  { value: "", label: "全部" },
  { value: "draft", label: "草稿" },
  { value: "promoted", label: "已转投放" },
  { value: "archived", label: "已归档" },
];

const STATUS_VARIANT: Record<
  string,
  "success" | "info" | "secondary" | "danger" | "warning"
> = {
  draft: "info",
  promoted: "success",
  archived: "secondary",
};

const STATUS_LABEL: Record<string, string> = {
  draft: "草稿",
  promoted: "已转投放",
  archived: "已归档",
};

// v2.13.x — must mirror the platforms actually registered in
// cmd/bitbase-kol/main.go (youtube, twitter, tiktok, rss, foresight,
// panews). The backend gates plan creation on Registry.Get(platform),
// so any value not registered returns "unknown platform" / 500. The old
// list sent "x" (backend stores X as "twitter", see adapter.go:19) plus
// facebook/instagram/medium/linkedin — none registered → guaranteed 500.
// Kept identical to Channels.tsx's PLATFORM_OPTIONS to avoid drift.
const PLATFORMS = [
  { value: "youtube", label: "YouTube" },
  { value: "twitter", label: "X" },
  { value: "tiktok", label: "TikTok" },
  { value: "rss", label: "RSS" },
  { value: "foresight", label: "Foresight" },
  { value: "panews", label: "PA News" },
];

const PAGE_SIZE = 50;

export default function Plans() {
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);

  const listQ = useQuery({
    queryKey: ["plans", { status, page }],
    queryFn: () => endpoints.plans({ status, page, limit: PAGE_SIZE }),
  });

  // Hide archived by default (same pattern as Campaigns.tsx — backend's
  // status filter accepts archived explicitly, but the empty filter
  // returns all rows including archived).
  const rawRows = listQ.data?.data ?? [];
  const rows =
    status === "" ? rawRows.filter((r) => r.status !== "archived") : rawRows;
  const hasNext = listQ.data?.next_page !== undefined;

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border bg-background px-6 py-3">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">投放筛选</h1>
          <span className="text-sm text-muted-foreground">
            第 {page} 页 · 本页 {rows.length} 条
          </span>
          <Button
            className="ml-auto"
            size="sm"
            onClick={() => setShowCreate(true)}
          >
            + 新建方案
          </Button>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="w-40"
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
          <div className="ml-auto flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1 || listQ.isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              上一页
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasNext || listQ.isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              下一页
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-background">
        {listQ.isLoading && (
          <p className="p-4 text-sm text-muted-foreground">加载中…</p>
        )}
        {listQ.isError && (
          <p className="p-4 text-sm text-destructive">
            加载失败:{(listQ.error as Error).message}
          </p>
        )}
        {listQ.data && rows.length === 0 && (
          <p className="p-4 text-sm text-muted-foreground">
            还没有投放方案 — 点右上角"新建方案",粘贴 agency 给的 list
            就能开始筛选。
          </p>
        )}
        {rows.length > 0 && (
          <Table>
            <THead>
              <TR>
                <TH>名称</TH>
                <TH>平台</TH>
                <TH>状态</TH>
                {/* v2.13.x — header was "候选 / 入选" but the cell renders
                    selected_count (bold) / candidate_count, i.e. 入选 / 候选.
                    Flipped the header to match the data + the bold emphasis
                    (selected = the actionable "made the cut" number). */}
                <TH className="text-right">入选 / 候选</TH>
                <TH className="text-right">预算</TH>
                <TH>创建</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((p) => (
                <PlanRow key={p.id} p={p} />
              ))}
            </TBody>
          </Table>
        )}
      </div>

      {showCreate && (
        <CreatePlanModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.refetch();
          }}
        />
      )}
    </div>
  );
}

function PlanRow({ p }: { p: PlanSummary }) {
  return (
    <TR>
      <TD>
        <Link to={`/plans/${p.id}`} className="font-medium hover:underline">
          {p.name}
        </Link>
        <div className="text-xs text-muted-foreground">
          #{p.id}
          {p.pending_enrichment_count > 0 && (
            <span className="ml-2 text-amber-600">
              · {p.pending_enrichment_count} 条 enrich 中
            </span>
          )}
        </div>
      </TD>
      <TD>
        <span className="text-xs uppercase text-muted-foreground">
          {p.platform}
        </span>
      </TD>
      <TD>
        <Badge variant={STATUS_VARIANT[p.status] ?? "secondary"}>
          {STATUS_LABEL[p.status] ?? p.status}
        </Badge>
      </TD>
      <TD className="text-right tabular-nums">
        <span className="font-medium">{p.selected_count}</span>
        <span className="text-muted-foreground"> / {p.candidate_count}</span>
      </TD>
      <TD className="text-right tabular-nums">
        {p.budget_usd != null ? (
          <span>${fmtUSD(p.budget_usd)}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TD>
      <TD className="text-xs text-muted-foreground">
        {fmtDate(p.created_at)}
        {p.created_by && (
          <span className="ml-1 opacity-70">· {p.created_by}</span>
        )}
      </TD>
    </TR>
  );
}

// -----------------------------------------------------------------------------
// New-plan modal.
// -----------------------------------------------------------------------------

function CreatePlanModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (created: PlanSummary) => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CreatePlanRequest>({
    name: "",
    platform: "youtube",
    budget_usd: undefined,
    notes: "",
  });

  const createMut = useMutation({
    mutationFn: () => endpoints.createPlan(form),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["plans"] });
      onCreated(created);
    },
  });

  const canSubmit = form.name.trim().length > 0 && form.platform.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-background shadow-lg">
        <div className="border-b border-border px-5 py-3">
          <h2 className="text-sm font-semibold">新建投放方案</h2>
        </div>
        <div className="space-y-3 px-5 py-4">
          <div>
            <label className="text-xs text-muted-foreground">
              方案名称 *
            </label>
            <Input
              autoFocus
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="例:May 2026 Agency List"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">
              平台 * (锁定,不可混排)
            </label>
            <Select
              value={form.platform}
              onChange={(e) =>
                setForm((f) => ({ ...f, platform: e.target.value }))
              }
            >
              {PLATFORMS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">
              预算 (USD,可选)
            </label>
            <Input
              type="number"
              step="100"
              value={form.budget_usd ?? ""}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  budget_usd: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                }))
              }
              placeholder="5000"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">
              备注 (可选)
            </label>
            <Input
              value={form.notes ?? ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, notes: e.target.value }))
              }
              placeholder="agency name / quarter / 备忘"
            />
          </div>
          {createMut.isError && (
            <p className="text-xs text-destructive">
              {(createMut.error as Error).message}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose}>
            取消
          </Button>
          <Button
            size="sm"
            disabled={!canSubmit || createMut.isPending}
            onClick={() => createMut.mutate()}
          >
            {createMut.isPending ? "创建中…" : "创建"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Format helpers — same style as Campaigns.tsx.
// -----------------------------------------------------------------------------

function fmtUSD(v: number): string {
  if (v === Math.trunc(v)) return v.toLocaleString();
  return v.toFixed(2);
}

function fmtDate(s: string): string {
  if (!s) return "—";
  return s.slice(0, 10);
}
